import { Button, Sheet } from '@gramas/ui'
import { useEffect, useState } from 'react'
import {
  applyUpdate,
  dismissInstall,
  type InstallState,
  promptInstall,
  readInstallState,
  subscribe,
  updateAvailable,
} from '../pwa/install.js'

/**
 * Two things that only matter occasionally, sharing one row.
 *
 * An update is offered rather than applied: reloading on its own would throw
 * away a half-filled sheet or a scan in progress. Installing is offered on the
 * second visit, because being asked to keep an app you have not used yet is how
 * install prompts got their reputation.
 */
export function InstallBanner() {
  const [install, setInstall] = useState<InstallState>({
    eligible: false,
    needsManualSteps: false,
  })
  const [update, setUpdate] = useState(false)
  const [steps, setSteps] = useState(false)

  useEffect(() => {
    const refresh = () => {
      setUpdate(updateAvailable())
      void readInstallState().then(setInstall)
    }
    refresh()
    return subscribe(refresh)
  }, [])

  if (update) {
    return (
      <button
        type="button"
        onClick={() => void applyUpdate()}
        className="flex shrink-0 items-center justify-center gap-2 border-b border-line bg-raised px-4 py-1.5 text-xs text-dim"
      >
        <span className="size-1.5 rounded-full bg-accent" aria-hidden="true" />
        Nauja versija paruošta · palieskite, kad atnaujintumėte
      </button>
    )
  }

  if (!install.eligible) return null

  return (
    <>
      <div className="flex shrink-0 items-center gap-2 border-b border-line bg-raised px-3 py-1.5 text-xs text-dim">
        <span className="size-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">Įdėkite „Gramą“ į pradžios ekraną</span>
        <button
          type="button"
          className="shrink-0 text-accent-text"
          onClick={() => {
            if (install.needsManualSteps) setSteps(true)
            else void promptInstall()
          }}
        >
          Įdiegti
        </button>
        <button
          type="button"
          aria-label="Atmesti"
          className="shrink-0 px-1 text-mute"
          onClick={() => void dismissInstall()}
        >
          <svg viewBox="0 0 16 16" className="size-3.5" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>
      </div>

      {/* Safari has no install API at all — on iOS the only route is the Share
          menu, so the app has to say where it is rather than offer a button. */}
      <Sheet
        open={steps}
        onOpenChange={setSteps}
        title="Įdiegti į pradžios ekraną"
        description="„Safari“ neturi mygtuko — tai daroma per dalijimosi meniu"
        footer={
          <Button variant="primary" block size="lg" onClick={() => setSteps(false)}>
            Supratau
          </Button>
        }
      >
        <ol className="flex flex-col gap-3 px-4 py-4 text-base text-ink">
          {[
            'Apačioje palieskite dalijimosi mygtuką — kvadratą su rodykle į viršų.',
            'Slinkite žemyn iki „Pridėti į pradžios ekraną“.',
            'Palieskite „Pridėti“ viršuje dešinėje.',
          ].map((step, i) => (
            <li key={step} className="flex gap-3">
              <span className="tnum flex size-6 shrink-0 items-center justify-center rounded-full border border-line text-sm text-mute">
                {i + 1}
              </span>
              <span className="pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
        <p className="px-4 pb-4 text-sm text-mute">
          Įdiegta programėlė veikia be interneto, nerodo naršyklės juostų, o dienyno duomenys niekur
          nedingsta.
        </p>
      </Sheet>
    </>
  )
}
